import React, { useEffect } from 'react';
import { getTickConflicts, earliestServiceTime } from '../lib/eventCompleteness';

/**
 * Structured kitchen info for an event: food service time + dietary counts.
 * Printed on the daily function sheet (red dietary box, covers-by-time strip).
 * Inputs are text (numeric keyboard) so "blank" stays blank rather than 0.
 *
 * Blank vs 0 matters (Oct 2026): blank = "not asked yet" and keeps the event
 * provisional; 0 = "asked, none". The "No dietary requirements" tick sets the
 * counts to 0 so reception records an answer without typing zeros.
 * "Drinks only" (foodServiceTime = 'none') removes food time and dietary from
 * the required fields.
 */
export default function KitchenFields({ data, onChange }) {
  const cls = 'w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';
  const missingCls = ' !border-red-500 !bg-red-50 ring-1 ring-red-400';
  const isBlank = (v) => v === '' || v === null || v === undefined;
  const set = (name, value) => onChange({ target: { name, value, type: 'text' } });

  const drinksOnly = data?.foodServiceTime === 'none';
  const musicOnly = !!data?.musicOnly;
  // NA = no meal chosen for this event, so dietary doesn't apply.
  const dietaryNA = !drinksOnly && !!data?.dietaryNA;
  const noDietary = !drinksOnly && !dietaryNA
    && ['dietaryVeg', 'dietaryVegan', 'dietaryCoeliac'].every((k) => !isBlank(data?.[k]) && parseInt(data[k], 10) === 0)
    && !String(data?.dietaryAllergens || '').trim();
  const dietaryAnswered = dietaryNA || ['dietaryVeg', 'dietaryVegan', 'dietaryCoeliac'].some((k) => !isBlank(data?.[k]))
    || !!String(data?.dietaryAllergens || '').trim() || !!String(data?.dietaryRequirements || '').trim();

  const num = (name, label) => (
    <div>
      <label className="block text-[11px] font-medium text-slate-500 mb-0.5">{label}</label>
      <input type="text" inputMode="numeric" name={name} value={data?.[name] ?? ''} onChange={onChange} disabled={drinksOnly || noDietary || dietaryNA}
        className={cls + (drinksOnly || noDietary || dietaryNA ? ' bg-slate-50 text-slate-400' : '') + (!drinksOnly && !musicOnly && !dietaryAnswered ? missingCls : '')} placeholder="—" />
    </div>
  );

  function toggleNoDietary(checked) {
    ['dietaryVeg', 'dietaryVegan', 'dietaryCoeliac'].forEach((k) => set(k, checked ? '0' : ''));
    if (checked) { set('dietaryAllergens', ''); set('dietaryNA', false); }
  }

  function toggleNA(checked) {
    set('dietaryNA', checked);
    if (checked) {
      ['dietaryVeg', 'dietaryVegan', 'dietaryCoeliac'].forEach((k) => set(k, ''));
      set('dietaryAllergens', '');
    }
  }

  // Food time comes from the service timeline when it has times (Nov 2026) —
  // asking for it again was duplicate entry. Kept in sync so kitchen/daily
  // sheet keep reading foodServiceTime. Manual box only when no timeline times.
  const timelineTime = earliestServiceTime(data);
  useEffect(() => {
    if (!drinksOnly && timelineTime && data?.foodServiceTime !== timelineTime) set('foodServiceTime', timelineTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineTime, drinksOnly]);

  // "Drinks only" ticked on a booking that clearly has food (ESB, 30 Oct 2026).
  const tc = getTickConflicts(data);
  const drinksWarn = drinksOnly ? [...tc.strong, ...tc.soft].filter((t) => t.startsWith('"Drinks only"')) : [];

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2">
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600" title="Drinks / room only — no food served. Food time and dietary are then not required, and the event is left out of kitchen covers.">
          <input type="checkbox" checked={drinksOnly} onChange={(e) => set('foodServiceTime', e.target.checked ? 'none' : '')} />
          Drinks only (no food)
        </label>
        {!drinksOnly && (
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600" title="Asked the client — nobody has dietary needs or allergies">
            <input type="checkbox" checked={noDietary} onChange={(e) => toggleNoDietary(e.target.checked)} />
            No dietary requirements
          </label>
        )}
        {!drinksOnly && (
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600" title="No meal has been chosen for this event — dietary numbers don't apply">
            <input type="checkbox" checked={dietaryNA} onChange={(e) => toggleNA(e.target.checked)} />
            Not Applicable (NA)
          </label>
        )}
      </div>
      {drinksWarn.length > 0 && (
        <div className="mb-2 rounded-lg border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <b>Check this:</b> {drinksWarn.join('; ')}. If food is being served, untick "Drinks only" and enter the food time – otherwise the kitchen won't see it as a food event.
        </div>
      )}
      {!drinksOnly && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-0.5" title="When food is served — drives the kitchen covers-by-time strip">Food served at{timelineTime ? '' : ' *'}</label>
            {timelineTime ? (
              <div className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-700" title="Taken from the earliest time in the service timeline above — change it there">
                {timelineTime} <span className="text-[10px] text-slate-400">from timeline</span>
              </div>
            ) : (
              <input type="time" name="foodServiceTime" value={data?.foodServiceTime || ''} onChange={onChange} className={cls + (!data?.foodServiceTime ? missingCls : '')} />
            )}
          </div>
          {num('dietaryVeg', 'Vegetarian')}
          {num('dietaryVegan', 'Vegan')}
          {num('dietaryCoeliac', 'Coeliac / GF')}
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-[11px] font-medium text-slate-500 mb-0.5">Allergens</label>
            <input type="text" name="dietaryAllergens" value={data?.dietaryAllergens || ''} onChange={onChange} disabled={noDietary || dietaryNA}
              className={cls + (noDietary || dietaryNA ? ' bg-slate-50 text-slate-400' : '') + (!musicOnly && !dietaryAnswered ? missingCls : '')} placeholder="e.g. 2 nut, 1 shellfish" />
          </div>
        </div>
      )}
      {!drinksOnly && !musicOnly && !dietaryAnswered && (
        <p className="text-[11px] text-red-700 font-semibold mt-1">Dietary needs required – enter numbers (0 is fine), allergens, or tick "No dietary requirements" or "Not Applicable (NA)".</p>
      )}
    </div>
  );
}
